import Panzoom from '@panzoom/panzoom'
import { useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import { scrollIntoView } from 'seamless-scroll-polyfill'
import styled from 'styled-components'
import { BasicButton, IconButton } from '@sevenrooms/core/ui-kit/form'
import { Icon } from '@sevenrooms/core/ui-kit/icons'
import { Box, Flex, HStack, Root, Tooltip, VStack } from '@sevenrooms/core/ui-kit/layout'
import { gxTheme, type ThemeColor } from '@sevenrooms/core/ui-kit/theme'
import { Text } from '@sevenrooms/core/ui-kit/typography'
import { DateTime, TimeOnly } from '@sevenrooms/timepiece'
import EventDetails from '../components/EventDetails'
import { hexToRgb } from '../utils/color'
import { InteractiveFloorplan } from './InteractiveFloorplan'
import InventoryList from './InventoryList'
import type { FloorPlanState } from './interactiveFloorplanReducer'
import type { EventData, VenueInfo, Inventory } from './types'
import type { Map as ImmutableMap } from 'immutable'

const HEADER_HEIGHT = '70px'

interface InteractiveFloorplanContainerProps {
  deviceSize: 'large' | 'small'
  dismiss: () => void
  eventData: EventData
  venueInfo: VenueInfo
  richText: string
  inventoryData: { [key: string]: Inventory }
  inventoryEdits: { [inventoryId: string]: unknown }
  soldInventory: Inventory[]
  boundToggleInventoryItem: (inventoryId: string) => void
  svg: string
  tieredEvents: string[]
  isPrivate: boolean
  isEventOpened: boolean
  date: string
  layoutHeight?: number
  pageBackgroundColor: string
  colorPrimary: string
  fontsColorButton: string
  fontsColorPrimary: string
  rgbaWidgetBackground: string
  userSelection: { date: string; eventId: string }
  svgInventory: FloorPlanState['svgInventory']
}

function InteractiveFloorplanContainer({
  deviceSize,
  dismiss,
  eventData,
  venueInfo,
  richText,
  inventoryData,
  inventoryEdits,
  layoutHeight,
  soldInventory,
  boundToggleInventoryItem,
  svg,
  tieredEvents,
  isPrivate,
  isEventOpened,
  date,
  pageBackgroundColor,
  colorPrimary,
  fontsColorButton,
  fontsColorPrimary,
  rgbaWidgetBackground,
  userSelection,
  svgInventory,
}: InteractiveFloorplanContainerProps) {
  const svgRef = useRef<HTMLDivElement>(null)
  const panzoom = useRef<ReturnType<typeof Panzoom>>()

  const isDesktopLayout = deviceSize === 'large'
  const backgroundColorKey = 'backgroundColor' as ThemeColor
  const textColorKey = 'textColor' as ThemeColor
  const eventDate = DateTime.from(userSelection.date).toDateOnly().formatNYearSMonthNDay()
  const eventStartTime = eventData.eventStartTime ? TimeOnly.from(eventData.eventStartTime).formatSTime() : ''
  const eventEndTime = eventData.eventEndTime ? TimeOnly.from(eventData.eventEndTime).formatSTime() : ''

  useEffect(() => {
    const templateStylesElem = isDesktopLayout && document.getElementById('templateStyles')
    const parentTable = document.getElementById('bw-table')

    if (templateStylesElem) {
      document.head.removeChild(templateStylesElem)
      if (parentTable) {
        parentTable.style.width = '100vw'
        parentTable.style.height = '100vh'
        parentTable.style.backgroundColor = pageBackgroundColor
      }
    }
    return () => {
      if (templateStylesElem) {
        if (parentTable) {
          parentTable.style.width = ''
          parentTable.style.height = ''
          parentTable.style.backgroundColor = ''
        }
        document.head.appendChild(templateStylesElem)
      }
    }
  }, [isDesktopLayout, pageBackgroundColor])

  useEffect(() => {
    const svgContainer = svgRef.current
    if (!isDesktopLayout && svgContainer) {
      panzoom.current = Panzoom(svgContainer, { minScale: 0.25, maxScale: 2 })
    }

    return () => {
      if (panzoom.current) {
        panzoom.current.destroy()
      }
    }
  }, [isDesktopLayout])

  const handleSvgLayerClick = (inventoryName: string, selectedDate: string) => {
    const element = svgInventory[selectedDate]?.find(inventory => inventory.inventoryName === inventoryName)?.elements[0]
    if (element) {
      if (isDesktopLayout) {
        scrollIntoView(element, { block: 'center', inline: 'center', behavior: 'smooth' })
        return
      }

      panzoom.current?.zoom(1)
      setTimeout(() => {
        if (!svgRef.current) {
          return
        }

        const inventoryLayerBoundingRect = element.getBoundingClientRect()
        const svgBoundingRect = svgRef.current.getBoundingClientRect()
        const parentBoundingRect = svgRef.current.parentElement?.getBoundingClientRect()
        const horizontalCenter =
          inventoryLayerBoundingRect.left -
          svgBoundingRect.left -
          (parentBoundingRect?.width || 0) / 2 +
          inventoryLayerBoundingRect.width / 2
        const verticalCenter =
          inventoryLayerBoundingRect.top -
          svgBoundingRect.top -
          (parentBoundingRect?.height || 0) / 2 +
          inventoryLayerBoundingRect.height / 2
        panzoom.current?.pan(-horizontalCenter, -verticalCenter, { animate: true })
      })
    }
  }

  const handleSvgInit = (element: Element) => {
    if (isDesktopLayout) {
      scrollIntoView(element, { block: 'center', inline: 'center', behavior: 'smooth' })
      return
    }

    setTimeout(() => {
      const svgElement = svgRef.current?.getElementsByTagName('svg')[0]

      if (!svgElement || !svgRef.current || !svgRef.current.parentElement) {
        return
      }

      const svgBoundingRect = svgElement.getBoundingClientRect()
      const parentBoundingRect = svgRef.current.parentElement.getBoundingClientRect()
      const widthRatio = parentBoundingRect.width / svgBoundingRect.width
      const heightRatio = parentBoundingRect.height / svgBoundingRect.height

      const ratioToApply = widthRatio < heightRatio ? widthRatio : heightRatio
      panzoom.current?.zoom(ratioToApply)

      setTimeout(() => {
        if (!svgRef.current || !svgRef.current.parentElement) {
          return
        }

        const svgBoundingRect = svgElement.getBoundingClientRect()
        const parentBoundingRect = svgRef.current.parentElement.getBoundingClientRect()
        const x = (parentBoundingRect.left - svgBoundingRect.left) / ratioToApply
        const y =
          (parentBoundingRect.top - svgBoundingRect.top + (parentBoundingRect.height / 2 - svgBoundingRect.height / 2)) / ratioToApply
        if (x !== undefined && y !== undefined) {
          panzoom.current?.pan(x, y)
        }
      }, 50)
    }, 50)
  }

  const header = (
    <>
      <HStack alignItems="center">
        <IconButton size="s" borderType="round" icon="GX-back" onClick={dismiss} iconColor="darkFont" iconBackgroundColor="lightFont" />
      </HStack>
      <HStack flex="1 1 auto" alignItems="center" justifyContent="center">
        <div>
          <StyledDateTimeWrapper textAlign="center">
            <Text fontSize="xl" color={textColorKey}>
              {eventDate}
            </Text>
          </StyledDateTimeWrapper>
          <StyledDateTimeWrapper textAlign="center">
            {eventStartTime && eventEndTime && (
              <Text fontSize="xs" color={textColorKey}>
                {eventStartTime} - {eventEndTime}
              </Text>
            )}
          </StyledDateTimeWrapper>
        </div>
      </HStack>
      <HStack alignItems="center">
        <Tooltip
          title={venueInfo.name}
          content={
            <VStack spacing="xs">
              <Text fontSize="l" color="lightFont">
                {eventData.name}
              </Text>
              <Text fontSize="s" color="lightFont">
                {eventDate}
              </Text>
              {eventStartTime && eventEndTime && (
                <Text fontSize="xs" color="lightFont">
                  {eventStartTime} - {eventEndTime}
                </Text>
              )}
            </VStack>
          }
        >
          <BasicButton aria-label="Additional Information">
            <Icon size="lg" name="GX-info-outline" color={textColorKey} />
          </BasicButton>
        </Tooltip>
      </HStack>
      <Box />
    </>
  )

  const desktopLayout = (
    <StyledCenteredVerticallyOnScreen width="100vw">
      <Flex justifyContent="center" height={layoutHeight}>
        <Flex display="block" flex="0 0 330px" overflowY="auto" borderRightColor="borders" backgroundColor={backgroundColorKey}>
          <HStack
            backgroundColor={backgroundColorKey}
            borderBottomColor="borders"
            p="s"
            position="sticky"
            spacing="s"
            top="none"
            zIndex={1}
            height={HEADER_HEIGHT}
          >
            {header}
          </HStack>
          <InventoryList
            inventoryData={inventoryData}
            inventoryEdits={inventoryEdits}
            isPrivate={isPrivate}
            isEventOpened={isEventOpened}
            date={date}
            tieredEvents={tieredEvents}
            onSvgLayerClick={handleSvgLayerClick}
          />
          <div style={{ backgroundColor: rgbaWidgetBackground }}>
            <EventDetails
              venueInfo={venueInfo}
              richText={richText}
              windowSize={0} // show details vertically
            />
          </div>
        </Flex>
        <StyledFlexWithNoScrollbar
          display="block"
          flex="0 1 auto"
          minWidth="200px"
          maxWidth="1024px"
          overflow="auto"
          backgroundColor={backgroundColorKey}
        >
          <InteractiveFloorplan
            availableInventory={Object.values(inventoryData)}
            soldInventory={soldInventory}
            boundToggleInventoryItem={boundToggleInventoryItem}
            selectedDate={userSelection.date}
            svgRef={svgRef}
            svg={svg}
            svgInventory={svgInventory}
            tieredEvents={tieredEvents}
            onSvgInit={handleSvgInit}
          />
        </StyledFlexWithNoScrollbar>
      </Flex>
    </StyledCenteredVerticallyOnScreen>
  )

  const mobileLayout = (
    <Box width="100vw" position="absolute" left="none">
      <Box backgroundColor={backgroundColorKey}>
        <HStack p="s" spacing="s" borderBottomColor="borders" height={HEADER_HEIGHT}>
          {header}
        </HStack>
        <Box height={`calc(50vh - ${HEADER_HEIGHT})`}>
          <InteractiveFloorplan
            availableInventory={Object.values(inventoryData)}
            soldInventory={soldInventory}
            boundToggleInventoryItem={boundToggleInventoryItem}
            selectedDate={userSelection.date}
            svgRef={svgRef}
            svg={svg}
            svgInventory={svgInventory}
            tieredEvents={tieredEvents}
            onSvgInit={handleSvgInit}
          />
        </Box>
        <Box height="50vh" overflowY="auto" borderTopColor="borders">
          <InventoryList
            inventoryData={inventoryData}
            inventoryEdits={inventoryEdits}
            isPrivate={isPrivate}
            isEventOpened={isEventOpened}
            date={date}
            tieredEvents={tieredEvents}
            onSvgLayerClick={handleSvgLayerClick}
          />
          <div style={{ backgroundColor: rgbaWidgetBackground }}>
            <EventDetails
              venueInfo={venueInfo}
              richText={richText}
              windowSize={0} // show details vertically
            />
          </div>
        </Box>
      </Box>
    </Box>
  )

  const themeOverride = {
    colors: {
      ...gxTheme.colors,
      backgroundColor: rgbaWidgetBackground,
      textColor: fontsColorPrimary,
      primaryBackground: rgbaWidgetBackground,
      primaryFont: fontsColorPrimary,
    },
    button: {
      primary: {
        backgroundColor: colorPrimary,
        textColor: fontsColorButton,
      },
    },
  }

  return (
    <Root theme="gx" themeOverride={themeOverride}>
      {isDesktopLayout ? desktopLayout : mobileLayout}
    </Root>
  )
}

const mapStateToProps = (state: {
  resize: { height?: number }
  interactiveFloorplan: FloorPlanState
  widgetSettings: {
    colorBackground?: string
    colorBackgroundOpacity?: string
    colorPrimary: string
    fontsColorButton: string
    fontsColorPrimary: string
    colorWidgetBackground: string
    colorWidgetBackgroundOpacity: string
  }
  venueInfo: VenueInfo
  userSelection: ImmutableMap<string, string>
}) => {
  const floorplanHeight = state.interactiveFloorplan.height
  const windowHeight = state.resize.height
  let layoutHeight
  if (floorplanHeight && windowHeight && floorplanHeight < windowHeight) {
    layoutHeight = floorplanHeight
  } else {
    layoutHeight = windowHeight
  }

  let pageBackgroundColor = ''
  const pageBackgroundColorSetting = state.widgetSettings.colorBackground
  const pageBackgroundColorOpacitySetting = state.widgetSettings.colorBackgroundOpacity
  if (pageBackgroundColorSetting && pageBackgroundColorOpacitySetting) {
    const rgba = hexToRgb(pageBackgroundColorSetting, pageBackgroundColorOpacitySetting)
    pageBackgroundColor = rgba ?? ''
  }

  const { colorPrimary, fontsColorButton, fontsColorPrimary, colorWidgetBackground, colorWidgetBackgroundOpacity } = state.widgetSettings

  return {
    venueInfo: state.venueInfo,
    layoutHeight,
    pageBackgroundColor,
    colorPrimary,
    fontsColorButton,
    fontsColorPrimary,
    rgbaWidgetBackground: hexToRgb(colorWidgetBackground, colorWidgetBackgroundOpacity) ?? '',
    userSelection: state.userSelection.toJS(),
    svgInventory: state.interactiveFloorplan.svgInventory,
  }
}

export default connect(mapStateToProps)(InteractiveFloorplanContainer)

const StyledFlexWithNoScrollbar = styled(Flex)`
  ::-webkit-scrollbar {
    display: none;
  }
`

const StyledCenteredVerticallyOnScreen = styled(Box)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
`

// Reset the font-size set by #bw-wrapper
const StyledDateTimeWrapper = styled(Box)`
  font-size: initial;
`
